import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { AuthService } from '@/services/auth.service'
import type { AuthState, UserRole } from '@/lib/types'

interface AuthContextType extends AuthState {
  role: UserRole
  roleLoading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const normalizeEmail = (email?: string | null) => (email ?? '').trim().toLowerCase()

/**
 * Determina el rol del usuario a partir del doc `config/roles`.
 * Estructura esperada: { vendedores: ["correo@ejemplo.com", ...] }.
 * Si el correo está en esa lista => 'vendedor' (acceso restringido a Ayudaventas).
 * En cualquier otro caso => 'admin' (ve todo).
 *
 * Robustez: reintenta la lectura (redes móviles inestables) y cachea el último
 * rol conocido por correo. Si la lectura falla del todo, NO cae a 'admin'
 * (eso dejaría a un vendedor viendo todo el sistema): usa el rol cacheado o,
 * si no hay, restringe a 'vendedor' (fail-closed).
 */
async function resolveRole(email: string | null): Promise<UserRole> {
  const target = normalizeEmail(email)
  if (!target) return 'admin'
  const cacheKey = `ayudaventas:role:${target}`

  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const snap = await getDoc(doc(db, 'config', 'roles'))
      const data = snap.exists() ? snap.data() : {}
      const list: string[] = Array.isArray(data?.vendedores) ? data.vendedores : []
      const role: UserRole = list.some((e) => normalizeEmail(e) === target) ? 'vendedor' : 'admin'
      try { localStorage.setItem(cacheKey, role) } catch { /* noop */ }
      return role
    } catch {
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)))
    }
  }

  // La lectura falló tras varios intentos: usar último rol conocido; si no hay,
  // restringir por seguridad (mejor bloquear de más que exponer el sistema).
  try {
    const cached = localStorage.getItem(cacheKey)
    if (cached === 'vendedor' || cached === 'admin') return cached
  } catch { /* noop */ }
  return 'vendedor'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  })
  const [role, setRole] = useState<UserRole>('admin')
  const [roleLoading, setRoleLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChange(async (user) => {
      setState({
        user,
        loading: false,
        error: null,
      })

      if (user) {
        setRoleLoading(true)
        const resolved = await resolveRole(user.email)
        setRole(resolved)
        setRoleLoading(false)
      } else {
        setRole('admin')
        setRoleLoading(false)
      }
    })

    return () => unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      await AuthService.signIn(email, password)
      // El onAuthStateChange actualizará el estado automáticamente
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }))
      throw error
    }
  }

  const signOut = async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      await AuthService.signOut()
      // El onAuthStateChange actualizará el estado automáticamente
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }))
      throw error
    }
  }

  return (
    <AuthContext.Provider value={{ ...state, role, roleLoading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
